import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { quotesApi, type Quote } from '@/services/quotes';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120_000; // Stop polling after 2 minutes

export const useQuote = (quoteId: string | null) => {
  const [pollStartTime, setPollStartTime] = useState<number | null>(null);

  // Reset poll timer when quoteId changes
  useEffect(() => {
    if (quoteId) {
      setPollStartTime(Date.now());
    } else {
      setPollStartTime(null);
    }
  }, [quoteId]);

  return useQuery({
    queryKey: ['quote', quoteId],
    queryFn: () => quotesApi.get(quoteId!),
    enabled: !!quoteId,
    refetchInterval: (query) => {
      const data = query.state.data as Quote | undefined;
      if (data?.status !== 'processing') return false;

      // Stop polling after timeout to prevent infinite requests
      if (pollStartTime && Date.now() - pollStartTime > POLL_TIMEOUT_MS) {
        return false;
      }

      return POLL_INTERVAL_MS;
    },
  });
};

export const useQuotes = () => {
  return useQuery({
    queryKey: ['quotes'],
    queryFn: () => quotesApi.list(),
    refetchOnMount: 'always',   // always fetch fresh data when navigating to the page
  });
};

export const useDeleteQuote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (quoteId: string) => quotesApi.delete(quoteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    },
  });
};
